import {HttpClient} from '@angular/common/http';
import {CaseOverviewHal} from '../../models/HAL/caseOverview-hal.model';
import {CaseHal} from '../../models/HAL/case-hal.model';
import {DocumentCollectionHal, DocumentHal} from '../../models/HAL/document-hal.model';
import {buildCaseZipFilename} from '../../utils/case-zip-filename';
import {
  bufferCount,
  catchError,
  concat,
  concatMap,
  from,
  ignoreElements,
  map,
  mergeMap,
  Observable,
  of,
  retry,
  switchMap,
  timer
} from 'rxjs';

const MAX_CONCURRENT_CASE_DOCUMENT_DOWNLOADS = 3;
const CASE_DOWNLOAD_BATCH_PAUSE_MS = 1000;
const DOWNLOAD_URL_REVOKE_DELAY_MS = 5000;

export type CaseZipDownloadFailureReason = 'NO_LINKS' | 'NO_FILES' | 'REQUEST_FAILED';

interface CaseZipDownloadRequest {
  caseId: string;
  caseName: string;
  caseHal?: CaseHal;
  documentsLink?: string;
  compressedFilesLink?: string;
}

export interface CaseZipDownloadResult {
  caseId: string;
  caseName: string;
  filename: string;
  zipBlob: Blob | null;
  failureReason: CaseZipDownloadFailureReason | null;
}

interface CaseZipBlobDownloadResult {
  zipBlob: Blob | null;
  filename: string;
  failureReason: CaseZipDownloadFailureReason | null;
}

/**
 * Business rule for overview bulk ZIP download.
 *
 * The action is enabled only when at least one case is selected and all selected cases are completed.
 */
export function canAllSelectedCasesBeDownloaded(selectedCases: CaseOverviewHal[]): boolean {
  return selectedCases.length > 0 && selectedCases.every(caseOverview => caseOverview.state?.toLowerCase() === 'completed');
}

/**
 * Downloads selected cases as individual ZIP streams.
 *
 * Cases are processed in small batches with a pause between batches to avoid triggering too many browser
 * downloads at once.
 */
export function downloadSelectedCasesDocumentsAsZip(http: HttpClient, selectedCases: CaseOverviewHal[]): Observable<CaseZipDownloadResult> {
  const totalBatchCount = Math.ceil(selectedCases.length / MAX_CONCURRENT_CASE_DOCUMENT_DOWNLOADS);
  return from(selectedCases).pipe(
    bufferCount(MAX_CONCURRENT_CASE_DOCUMENT_DOWNLOADS),
    concatMap((caseBatch, batchIndex) => {
      const batchDownloadResults$ = from(caseBatch).pipe(
        mergeMap(
          caseOverview => downloadSelectedCaseDocumentsAsZip(http, caseOverview),
          MAX_CONCURRENT_CASE_DOCUMENT_DOWNLOADS
        )
      );
      const isLastBatch = batchIndex === totalBatchCount - 1;
      if (isLastBatch) {
        return batchDownloadResults$;
      }
      return concat(batchDownloadResults$, timer(CASE_DOWNLOAD_BATCH_PAUSE_MS).pipe(ignoreElements()));
    })
  );
}

/**
 * Resolves links needed to download one case ZIP.
 */
function downloadSelectedCaseDocumentsAsZip(http: HttpClient, caseOverview: CaseOverviewHal): Observable<CaseZipDownloadResult> {
  const caseId = caseOverview.identification;
  const caseName = caseOverview.name;
  const caseSelfLink = caseOverview._links.getLink('self')?.href;
  if (!caseSelfLink) {
    return of<CaseZipDownloadResult>({
      caseId,
      caseName,
      filename: buildCaseZipFilename(undefined, [], 0),
      zipBlob: null,
      failureReason: 'NO_LINKS'
    });
  }

  return http.get<CaseHal>(caseSelfLink).pipe(
    retry(1),
    map(caseDetail => {
      const caseHal = new CaseHal(caseDetail);
      return {
        caseId,
        caseName,
        caseHal,
        documentsLink: caseHal._links.getLink('getDocuments')?.href,
        compressedFilesLink: caseHal._links.getLink('compressedFiles')?.href
      };
    }),
    switchMap(caseDownloadRequest => downloadCaseDocumentsAsZip(http, caseDownloadRequest)),
    catchError(() => of<CaseZipDownloadResult>({
      caseId,
      caseName,
      filename: buildCaseZipFilename(undefined, [], 0),
      zipBlob: null,
      failureReason: 'REQUEST_FAILED'
    }))
  );
}

/**
 * Loads downloadable documents for one case and requests the compressed ZIP from the backend.
 *
 * The backend endpoint accepts POST with selected document ids, so we first fetch documents, filter to those
 * with file ids, and then request the archive.
 */
function downloadCaseDocumentsAsZip(http: HttpClient, caseDownloadRequest: CaseZipDownloadRequest): Observable<CaseZipDownloadResult> {
  const caseHal = caseDownloadRequest.caseHal;
  if (!caseDownloadRequest.documentsLink || !caseDownloadRequest.compressedFilesLink) {
    return of<CaseZipDownloadResult>({
      caseId: caseDownloadRequest.caseId,
      caseName: caseDownloadRequest.caseName,
      filename: buildCaseZipFilename(caseHal, [], 0),
      zipBlob: null,
      failureReason: 'NO_LINKS'
    });
  }

  const documentsUrl = new URL(caseDownloadRequest.documentsLink);
  documentsUrl.searchParams.set('paged', 'false');

  return http.get<DocumentCollectionHal>(documentsUrl.href).pipe(
    retry(1),
    map(documentCollection => {
      const documents = new DocumentCollectionHal(documentCollection)._embedded?.documents ?? [];
      return documents.filter(document => !!document.fileMetadata?.fileId);
    }),
    switchMap(downloadableDocuments => {
      // Whole-case download: "selected" maps to all downloadable documents in the case.
      const filename = buildCaseZipFilename(caseHal, downloadableDocuments, downloadableDocuments.length);
      if (downloadableDocuments.length === 0) {
        return of<CaseZipBlobDownloadResult>({
          zipBlob: null,
          filename,
          failureReason: 'NO_FILES'
        });
      }
      const downloadableDocumentIds = downloadableDocuments.map((document: DocumentHal) => document.identification);
      return http.post<Blob>(caseDownloadRequest.compressedFilesLink!, downloadableDocumentIds, {responseType: 'blob' as 'json'}).pipe(
        retry(1),
        map(zipBlob => ({
          zipBlob,
          filename,
          failureReason: null
        } as CaseZipBlobDownloadResult)),
        catchError(() => of<CaseZipBlobDownloadResult>({
          zipBlob: null,
          filename,
          failureReason: 'REQUEST_FAILED'
        }))
      );
    }),
    map(downloadResult => ({
      caseId: caseDownloadRequest.caseId,
      caseName: caseDownloadRequest.caseName,
      filename: downloadResult.filename,
      zipBlob: downloadResult.zipBlob,
      failureReason: downloadResult.failureReason
    })),
    catchError(() => of({
      caseId: caseDownloadRequest.caseId,
      caseName: caseDownloadRequest.caseName,
      filename: buildCaseZipFilename(caseHal, [], 0),
      zipBlob: null,
      failureReason: 'REQUEST_FAILED'
    } as CaseZipDownloadResult))
  );
}

/**
 * Triggers browser download for a ZIP blob with a controlled filename.
 *
 * URL cleanup is delayed slightly to avoid browser race conditions.
 */
export function downloadZipBlob(blob: Blob, filename: string): void {
  const blobToDownload = blob.type ? blob : new Blob([blob], {type: 'application/octet-stream'});
  const url = globalThis.URL.createObjectURL(blobToDownload);
  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  anchor.download = filename;
  anchor.href = url;
  document.body.appendChild(anchor);
  anchor.click();
  setTimeout(() => {
    globalThis.URL.revokeObjectURL(url);
    anchor.remove();
  }, DOWNLOAD_URL_REVOKE_DELAY_MS);
}

/**
 * Maps technical/business download failures to dedicated snackbar translation keys.
 */
export function getCaseDownloadFailureSnackbarKey(failureReason: CaseZipDownloadFailureReason | null): string {
  if (failureReason === 'REQUEST_FAILED') {
    return 'casesOverview.snackbars.downloadCaseRequestFailed';
  }
  return 'casesOverview.snackbars.downloadCaseUnavailable';
}
